import React from 'react';
import { Box, IconButton, Tooltip, Divider, Slider, Typography } from '@mui/material';
import NearMeRoundedIcon from '@mui/icons-material/NearMeRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import CropSquareRoundedIcon from '@mui/icons-material/CropSquareRounded';
import HorizontalRuleRoundedIcon from '@mui/icons-material/HorizontalRuleRounded';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import GestureRoundedIcon from '@mui/icons-material/GestureRounded';
import PentagonRoundedIcon from '@mui/icons-material/PentagonRounded';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import TitleRoundedIcon from '@mui/icons-material/TitleRounded';
import WbIncandescentRoundedIcon from '@mui/icons-material/WbIncandescentRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import DirectionsRunRoundedIcon from '@mui/icons-material/DirectionsRunRounded';
import WavesRoundedIcon from '@mui/icons-material/WavesRounded';
import { useTranslation } from 'react-i18next';
import { useInstatDesign } from '../theme/instatDesign';

// Tool descriptors. Labels are resolved via i18n at render time so they live
// here only by id — the i18n key `instat.telestration.tools.<id>` gives the
// translated label (with the shortcut suffix already baked in).
const TOOL_GROUPS = [
  {
    groupKey: 'drawing',
    tools: [
      { id: 'select',      Icon: NearMeRoundedIcon },
      { id: 'arrow',       Icon: ArrowRightAltRoundedIcon },
      { id: 'curvedArrow', Icon: GestureRoundedIcon },
      { id: 'line',        Icon: HorizontalRuleRoundedIcon },
      { id: 'circle',      Icon: RadioButtonUncheckedRoundedIcon },
      { id: 'rect',        Icon: CropSquareRoundedIcon },
      { id: 'pentagon',    Icon: PentagonRoundedIcon },
      { id: 'polygon',     Icon: HexagonOutlinedIcon },
      { id: 'spotlight',   Icon: WbIncandescentRoundedIcon },
      { id: 'text',        Icon: TitleRoundedIcon },
    ],
  },
  {
    groupKey: 'players',
    tools: [
      { id: 'playerToken',   Icon: PersonRoundedIcon },
      { id: 'playerDuo',     Icon: PeopleRoundedIcon },
      { id: 'playerMove',    Icon: DirectionsRunRoundedIcon },
      { id: 'playerDribble', Icon: WavesRoundedIcon },
    ],
  },
];

const PALETTE = [
  '#FFD60A', // yellow
  '#FF3B30', // red
  '#34C759', // green
  '#007AFF', // blue
  '#FF9500', // orange
  '#FFFFFF', // white
  '#000000', // black
];

export default function TelestrationToolbar({ scene, sceneApi }) {
  const d = useInstatDesign();
  const { t } = useTranslation();

  const btnSx = (active) => ({
    width: 42,
    height: 42,
    borderRadius: `${d.radius.md}px`,
    border: `1px solid ${active ? d.accent : 'transparent'}`,
    bgcolor: active ? d.accentSoft : 'transparent',
    color: active ? d.accent : d.colors.text,
    '&:hover': { bgcolor: active ? d.accentSoft : d.colors.canvasAlt },
  });

  return (
    <Box
      sx={{
        width: 84,
        flexShrink: 0,
        bgcolor: d.colors.surface,
        borderLeft: `1px solid ${d.colors.divider}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1.5,
        gap: 0.75,
        overflowY: 'auto',
      }}
    >
      {TOOL_GROUPS.map((group, gi) => (
        <React.Fragment key={group.groupKey}>
          {gi > 0 && <Divider flexItem sx={{ my: 0.5 }} />}
          <Typography sx={{ ...d.typo.micro, color: d.colors.textTertiary }}>
            {t(`instat.telestration.groups.${group.groupKey}`)}
          </Typography>
          {group.tools.map((tool) => (
            <Tooltip
              key={tool.id}
              title={t(`instat.telestration.tools.${tool.id}`)}
              placement="left"
            >
              <IconButton
                size="small"
                sx={btnSx(scene.activeTool === tool.id)}
                onClick={() => sceneApi.setTool(tool.id)}
              >
                <tool.Icon />
              </IconButton>
            </Tooltip>
          ))}
        </React.Fragment>
      ))}

      <Divider flexItem sx={{ my: 1 }} />

      {/* Color palette */}
      <Typography sx={{ ...d.typo.micro, color: d.colors.textTertiary }}>
        {t('instat.telestration.color')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, px: 0.5 }}>
        {PALETTE.map((c) => (
          <Box
            key={c}
            onClick={() => sceneApi.setColor(c)}
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: c,
              border: scene.color === c ? `2px solid ${d.accent}` : `1px solid ${d.colors.divider}`,
              cursor: 'pointer',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            }}
          />
        ))}
      </Box>

      <Divider flexItem sx={{ my: 1 }} />

      {/* Stroke width */}
      <Typography sx={{ ...d.typo.micro, color: d.colors.textTertiary }}>
        {t('instat.telestration.stroke')}
      </Typography>
      <Box sx={{ width: 60, px: 0.5 }}>
        <Slider
          size="small"
          min={1}
          max={10}
          step={1}
          value={scene.strokeWidth}
          onChange={(_, v) => sceneApi.setStrokeWidth(v)}
          valueLabelDisplay="auto"
        />
      </Box>

      <Divider flexItem sx={{ my: 1 }} />

      <Tooltip title={t('instat.telestration.undo')} placement="left">
        <span>
          <IconButton
            size="small"
            sx={btnSx(false)}
            onClick={sceneApi.undo}
            disabled={!sceneApi.canUndo}
          >
            <UndoRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('instat.telestration.redo')} placement="left">
        <span>
          <IconButton
            size="small"
            sx={btnSx(false)}
            onClick={sceneApi.redo}
            disabled={!sceneApi.canRedo}
          >
            <RedoRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('instat.telestration.deleteSelection')} placement="left">
        <span>
          <IconButton
            size="small"
            sx={btnSx(false)}
            onClick={() => sceneApi.deleteObject()}
            disabled={!scene.selectedId}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
